/**
 * BOOT — the back half of the boot sequence (docs/app-shell-arbeitsauftrag.md
 * §4, from "App Manifest laden" onward): wires an already-constructed
 * `Space` to `@qu/app-core`'s `AppRuntime`/`HashRouter` and
 * `@qu/app-renderer`'s `renderPage()`, so every hash-route change re-resolves
 * and re-renders. The FRONT half of the boot sequence (Identity -> Space ->
 * Storage -> Transport/Relay - docs' own diagram) is deliberately NOT this
 * function's job: it needs real network/`localStorage` glue (see
 * `identity.js`/`shell.js`), which would make this untestable without a
 * live relay. Callers (a real browser via `shell.js`, or a test/demo via an
 * in-process `Space`) construct the `Space` however is appropriate for
 * them and hand it here - this function knows nothing about HOW it was
 * built, only that it behaves like one.
 */
import { AppRuntime, HashRouter, PlatformRuntime, ContentResolver, createApp, appManifestKind, adminAppManifestKind, adminPageKind, adminTemplateKind, adminStyleKind, adminRouteRegistryKind, adminViewKind, globalAppAnchor } from '@qu/app-core';
import { QuCrypto } from '@qu/core';
import { deriveOwnerNodeId } from '@qu/space-core';
import { renderPage } from '@qu/app-renderer';
import { wireAdminConsole } from './admin-actions.js';
import { wireCms } from './cms-actions.js';
import { wireViews } from './view-actions.js';
import { wireRichText } from './rich-text-actions.js';
import { wireInstalledApps, provisionPersonalInstance, wirePersonalUpdateBanner } from './installed-apps-actions.js';
import { installCms } from '../cms-bundle.js';
import { bindLocalRichText } from '@qu/space-editor-prosemirror';

/**
 * Passed as `AppRuntime`'s `kinds` override for `realm: 'global'` routes -
 * see `resolver.js`'s own doc comment on what this parametrizes. Shared by
 * EVERY global app (they all use the SAME Kind set, told apart only by
 * their anchor - see kinds.js's own "GLOBAL APP CONTENT" doc comment).
 * `routeRegistryKind: adminRouteRegistryKind` was added specifically for
 * `wireViews()`'s own `'pages'` source adapter (`@qu/app-core`'s
 * `view-sources.js`) - a global app's OWN Views need to resolve routes
 * against ITS OWN registry Kind, not the default `qu-route-registry`
 * (wrong ACL mode entirely for a global app); `AppRuntime.resolveRoute()`
 * itself still never touches this key.
 */
const GLOBAL_KINDS = {
  appManifestKind: adminAppManifestKind,
  pageKind: adminPageKind,
  templateKind: adminTemplateKind,
  styleKind: adminStyleKind,
  routeRegistryKind: adminRouteRegistryKind,
  viewKind: adminViewKind,
};

/** @param {{mountEl: Element, doc: Document, platform: PlatformRuntime, space: import('@qu/space-core').Space}} params - shown when no registered app's prefix (nor a well-formed owner id) matches the current route. The one piece of `startPlatform()` UI that ISN'T Qu content: by definition nothing here resolved, so there is no content to fetch it from - same "Framework Default" posture `@qu/app-renderer` already takes for a single app's own unresolved routes. */
async function renderLandingPage({ mountEl, doc, platform, space }) {
  // Filters out `mode: 'off'` global apps - kinds.js's own doc comment on the four states requires
  // an "off" app to be INDISTINGUISHABLE from one never registered at all; a landing-page link that
  // 404s the moment it's clicked would violate that for ordinary visitors (an admin still sees it,
  // deliberately, in the admin console's own apps list - that one needs to stay reachable to turn it
  // back on).
  const apps = (await platform.resolveApps({ timeout: 1500 })).filter((a) => !(a.realm === 'global' && a.mode === 'off'));
  const container = doc.createElement('div');
  container.style.cssText = 'font-family: sans-serif; max-width: 40rem; margin: 2rem auto; line-height: 1.5; padding: 0 1rem;';
  const h1 = doc.createElement('h1');
  h1.textContent = 'Qu App Shell';
  container.appendChild(h1);
  const p = doc.createElement('p');
  p.textContent = apps.length > 0 ? 'Verfügbare Anwendungen:' : 'Noch keine Anwendung auf dieser Plattform installiert.';
  container.appendChild(p);
  if (apps.length === 0) {
    // The one thing this page CAN say without any Qu content to resolve (this IS the fallback for
    // when there's genuinely nothing yet, including at #/admin BEFORE the "admin" alias has ever
    // been registered - a plain relay restart alone never gets you here, an install step is always
    // required first, same "framework never silently assumes a Package is installed" posture the
    // rest of this file already takes for a single unresolved route).
    const hint = doc.createElement('p');
    hint.innerHTML = 'Am schnellsten: <code>npm run bootstrap:platform</code> (siehe root <code>README.md</code>s "Deploying the App Shell") - installiert die Admin-Konsole (dann erreichbar unter <code>#/admin</code>) und eine CMS-verwaltete Demo-App in einem Schritt.';
    container.appendChild(hint);
  }
  const list = doc.createElement('ul');
  for (const app of apps) {
    const li = doc.createElement('li');
    const a = doc.createElement('a');
    a.href = `#/${app.prefix}/`;
    a.textContent = app.name ?? app.prefix;
    li.appendChild(a);
    list.appendChild(li);
  }
  container.appendChild(list);

  // A direct link to THIS visitor's own bare-pubkey space (this file's own doc comment on
  // `startPlatform()`'s "SELF-PROVISIONING ALSO HAPPENS at the platform's own UNREGISTERED...
  // fallback") - `QuCrypto.toBase64Url()`, NEVER the plain `toBase64()` a human might otherwise
  // reach for (e.g. `window.Qu.pub`, meant for CONFIG values like `QU_RELAY_ADMINS`, not URLs): a
  // raw base64 pubkey routinely contains `/` and `+`, which a hash-route silently mis-splits into
  // extra path segments instead of erroring - a real, reported trap, not a hypothetical one. This
  // link is what makes the "own space" feature actually discoverable/clickable without anyone ever
  // needing to construct or paste a pubkey into a URL by hand at all.
  const ownHeading = doc.createElement('p');
  const ownLink = doc.createElement('a');
  ownLink.href = `#/${QuCrypto.toBase64Url(space.identity.signingPub)}/`;
  ownLink.textContent = 'Dein eigener Bereich';
  ownHeading.appendChild(ownLink);
  container.appendChild(ownHeading);
  mountEl.replaceChildren(container);
}

/**
 * Shown at `#/admin` for anyone whose OWN identity isn't currently a
 * relay-admin (`space.isRelayAdmin()`), INSTEAD of the real admin console -
 * a real, requested UX gap this closes: the admin console's own CONTENT is
 * `'public'`-visibility by design (kinds.js's own "GLOBAL APP CONTENT" doc
 * comment - readable by anyone, WRITE is what the relay actually gates,
 * exactly like a rejected write elsewhere in this framework), but nothing
 * client-side previously reflected that distinction - an ordinary visitor
 * saw the exact same "register an app"/apps-list UI a relay-admin does,
 * with every write silently rejected the instant they tried one. Purely
 * cosmetic, purely client-side (this Space's OWN independent `relayAdmins`
 * view - never trusts the relay's own say-so, same posture as everywhere
 * else) - the relay's write-ACL is and remains the only REAL boundary,
 * unchanged by this function existing at all.
 */
function renderAdminUnauthorized({ mountEl, doc }) {
  const container = doc.createElement('div');
  container.style.cssText = 'font-family: sans-serif; max-width: 40rem; margin: 2rem auto; line-height: 1.5; padding: 0 1rem;';
  const h1 = doc.createElement('h1');
  h1.textContent = 'Kein Zugriff';
  container.appendChild(h1);
  const p = doc.createElement('p');
  p.textContent = 'Diese Seite ist nur für Relay-Admins sichtbar. Deine aktuelle Identität ist keine.';
  container.appendChild(p);
  const a = doc.createElement('a');
  a.href = '#/';
  a.textContent = 'Zur Startseite';
  container.appendChild(a);
  mountEl.replaceChildren(container);
}

/**
 * Recognizes a `realm: 'global'`, `mode: 'multiuser'` app's EXPLICIT
 * per-user sub-namespace (kinds.js's own doc comment on the four states) -
 * `/u/<ref>/<rest>` where `ref` is either the literal string `"me"` (the
 * CURRENTLY signed-in identity - the default anyway, see `startPlatform()`,
 * so this form is rarely typed by hand) or another identity's own
 * base64url-encoded pubkey (the SAME encoding `PlatformRuntime`'s own
 * top-level "unregistered prefix = literal owner id" fallback already
 * uses - reading it is always public, same as any other content here) -
 * the one case a bare prefix genuinely can't express: looking at someone
 * ELSE's own space on purpose. Returns `null` for anything else, which
 * `startPlatform()` then treats as an IMPLICIT `{ref: 'me', ...}` - see
 * that function's own doc comment on why the default flipped away from
 * the global shell (discoverability: a first-time visitor has no reason to
 * know or paste their own pubkey just to reach their OWN space).
 *
 * ALSO recognizes the SAME thing spelled without the `/u/` marker at all -
 * `/<pubkey>/<rest>` - for an ordinary `mode: 'global'`/`'personal'` app's
 * own ADDITIVE personal route (`renderMultiUserRoute()`'s own doc comment
 * on `routeNamespace`): `#/blog/<pub>/` for that identity's own feed,
 * `#/blog/<pub>/<slug>` for one of their own posts - `docs/example-apps.md`'s
 * own worked example. Safe to accept unconditionally, no `/u/` needed to
 * disambiguate: a real base64url-encoded 32-byte pubkey (`QuCrypto.
 * fromBase64Url()`, the SAME check `PlatformRuntime`'s own top-level
 * "unregistered prefix = literal owner id" fallback already makes) is a
 * ~43-character string that will not, in practice, collide with an
 * ordinary content route someone typed by hand. `"me"` is NOT a valid
 * pubkey and never matches this shorter form on purpose - it stays
 * reachable only via the explicit `/u/me/` spelling (this file's own
 * `renderMultiUserRoute()` doc comment on why self-provisioning is
 * deliberately gated on `ref === 'me'` specifically, never inferred from
 * "some 32-byte value that happens to be this identity's own pubkey").
 * @param {string} subPath
 * @returns {{ref: string, userSubPath: string}|null}
 */
function parseMultiUserSubPath(subPath) {
  const explicit = /^\/u\/([^/]+)(\/.*)?$/.exec(subPath ?? '');
  if (explicit) return { ref: explicit[1], userSubPath: explicit[2] || '/' };
  const bare = /^\/([^/]+)(\/.*)?$/.exec(subPath ?? '');
  if (bare && looksLikePubkey(bare[1])) return { ref: bare[1], userSubPath: bare[2] || '/' };
  return null;
}

/** `parseMultiUserSubPath()`'s own "does this segment even look like a pubkey" check - `resolveUserRef()`'s own doc comment already does the REAL, authoritative decode; this is just a cheaper pre-filter so an ordinary route segment (e.g. a Blog's own global "/post/hallo") is never even attempted as one. */
function looksLikePubkey(segment) {
  try {
    return QuCrypto.fromBase64Url(segment).length === 32;
  } catch {
    return false;
  }
}

/**
 * Recognizes `#/admin/<appPrefix>/<rest>` - the relay-admin-only route to
 * another registered `realm: 'global'` app's OWN global shell content
 * (`renderGlobalShell()`, right below) - `startPlatform()`'s `"admin"`
 * branch tries this BEFORE falling back to rendering the admin console's
 * own UI, so `#/admin/cms/` reaches the "cms" app's global landing page and
 * `#/admin/cms/cms` its global CMS editor, exactly the counterpart to how
 * `#/<prefix>/...` used to reach it directly before `mode: 'multiuser'`
 * claimed the bare prefix for each visitor's OWN space instead (see this
 * file's own top doc comment on `startPlatform()`). Returns `null` for a
 * bare `/` (the admin console's OWN root) - callers only ever consult this
 * from within the already-matched `"admin"` prefix, so an empty match here
 * correctly means "render the admin console itself," not "some app named
 * the empty string."
 * @param {string} subPath - `match.subPath` for `prefix === 'admin'`.
 * @returns {{appPrefix: string, appSubPath: string}|null}
 */
function parseAdminSubPath(subPath) {
  const match = /^\/([^/]+)(\/.*)?$/.exec(subPath ?? '');
  if (!match) return null;
  return { appPrefix: match[1], appSubPath: match[2] || '/' };
}

/** `parseMultiUserSubPath()`'s own `ref` resolved to a real pubkey, or `null` if it's neither `"me"` nor a well-formed base64url pubkey. */
function resolveUserRef(ref, space) {
  if (ref === 'me') return space.identity.signingPub;
  try {
    const pub = QuCrypto.fromBase64Url(ref);
    return pub.length === 32 ? pub : null;
  } catch {
    return null;
  }
}

/**
 * `renderMultiUserRoute()`'s own self-provisioning step - only calls
 * `createApp()`/`installCms()` if this identity genuinely has no manifest
 * yet (`ContentResolver.resolveManifest()`, the same check any ordinary
 * reader already uses, given a generous 1500ms timeout - real, deployment-
 * observed reasoning below, not an arbitrary number).
 *
 * A REAL RACE THIS ONCE HAD, caught before shipping: an earlier version
 * hand-rolled its OWN "does it exist" check via a raw `space.useNode()` +
 * bounded `node.meta` poll, released immediately after - which, for a
 * Node THIS SAME identity just created moments earlier (`createNode()`
 * never refcounts its own creation - the first `useNode()`/`release()`
 * pair ANY reader does afterward tears the local Y.Doc back down, exactly
 * the "torn down after every read, forces a full resync every time"
 * problem `dev.js`'s own `getOrSyncRegistryNode()` doc comment describes
 * for registries, just for an ordinary owner Node this time), needed a
 * REAL round-trip through the relay's own mirror to become visible again -
 * not instant even in-process, and the hand-rolled check's own bound
 * (400ms) occasionally lost that race, causing a second, spurious
 * `createApp()`/`installCms()` call for a manifest that already existed
 * (a real, observed bug, not hypothetical - reproduced via the exact
 * `#/cms/u/me/` -> `#/cms/u/me/cms` navigation this function exists for).
 * `resolveManifest()`'s own generous, already-established timeout absorbs
 * that same round-trip reliably, with no bespoke logic to get subtly
 * wrong. A genuinely CONCURRENT double-call (two tabs, same identity, same
 * exact instant) could still race here - an accepted, low-stakes residual
 * (a rare, cosmetic duplicate on someone's own first-ever visit, no
 * different from `createApp()`/`createTemplate()`/`createPage()` having no
 * built-in protection against being called twice concurrently either),
 * not something worth a heavier lock for.
 */
async function ensureSelfProvisioned(space, ownerPub) {
  const resolver = new ContentResolver(space, { appAdminPub: ownerPub });
  const manifest = await resolver.resolveManifest({ timeout: 1500 });
  if (!manifest) {
    await createApp(space, { name: 'Mein Bereich', rootTemplate: null, defaultRoute: '/' });
    await installCms(space);
  }
}

/**
 * Renders one user's own sub-namespace within a `realm: 'global'` app - an
 * ORDINARY `AppRuntime`/`wireCms()` pair, exactly like a single-owner app
 * (`startApp()`'s own posture), just addressed at `ownerPub` instead of a
 * `qu-platform-apps`-registered `appAdminPub` - the whole point of this
 * mechanism: self-owned `'content'`-ACL Kinds need no relay-admin
 * cooperation or per-app registration to work AT ALL, they only need an
 * agreed-upon URL SHAPE to be discoverable, which is all this function
 * provides. Two DIFFERENT callers use it, told apart by `routeNamespace`:
 *
 *   - `mode: 'multiuser'` (`routeNamespace` omitted/`''`) - the bare
 *     `#/<prefix>/` prefix ITSELF means "my own space" (the flip this
 *     mode is named for - `startPlatform()`'s own top doc comment). Route
 *     resolves at the bare `userSubPath`, unprefixed - by design, so
 *     `#/cms/u/me/` and `#/<any-other-multiuser-app>/u/me/` reach the
 *     EXACT SAME identity-addressed content regardless of which app's URL
 *     got you there (`deriveContentNodeId(ownerPub, kind, route)` has no
 *     prefix in it at all) - ONE canonical personal space per identity,
 *     not one per app. `ensureSelfProvisioned()`'s generic "Mein Bereich"
 *     CMS starter is what a brand-new visitor sees here.
 *   - An ORDINARY `mode: 'global'` app's own ADDITIVE `/u/<ref>/` (the user's
 *     own framing: "zusätzlich zur globalen Route, optional") -
 *     `routeNamespace: '/'+prefix` this time, so the resolved route becomes
 *     `/<prefix><userSubPath>` instead of bare `userSubPath` - this is what
 *     keeps a personal Guestbook/Blog instance from colliding with this
 *     SAME identity's own "Mein Bereich" root OR a DIFFERENT app's own
 *     personal instance (all three are the exact same self-owned Kind set,
 *     told apart only by ROUTE now, not by owner). Never replaces what the
 *     bare `#/<prefix>/` means - that still always resolves the GLOBAL
 *     shell (`renderGlobalShell()`), unchanged, for every visitor.
 *     `personalBundle` (the app's OWN `qu-platform-apps` entry - `dev.js`'s
 *     `registerApp()` own doc comment) says WHICH reference app's own
 *     personal-instance installer to self-provision instead of the generic
 *     CMS starter - `installed-apps-actions.js`'s `provisionPersonalInstance()`.
 *     `config` (this app's own `qu-platform-apps` `config`, `match.config`
 *     straight off `PlatformRuntime.resolveForPath()` - kinds.js's own doc
 *     comment) is threaded down to that SAME installer, so a visitor's
 *     personal instance follows whatever install-time option (Blog's own
 *     `routeScheme`) the relay-admin picked for the global one.
 *
 * SELF-PROVISIONING, `ref === "me"` ONLY: a brand-new visitor's own
 * identity has no content at this route yet the very first time they reach
 * it - rather than a 404 (technically correct, but a dead end with no way
 * to fix itself), this creates some, using nothing but THIS identity's own
 * already-connected `space` - the same calls any install script already
 * makes, just triggered by a first visit instead of an operator running
 * one. Never done for someone else's `ref` (an ordinary visitor reading
 * Alice's still-empty page must never conjure content into Alice's OWN
 * name) - reading stays side-effect-free regardless of what's actually
 * there.
 */
async function renderMultiUserRoute({ space, mountEl, window, styleId, resolveTimeout, ref, userSubPath, routeNamespace = '', personalBundle, config, richTextBind }) {
  const timeoutOpt = resolveTimeout ? { timeout: resolveTimeout } : undefined;
  const ownerPub = resolveUserRef(ref, space);
  if (!ownerPub) {
    renderPage({ mountEl, doc: window.document, templateHtml: null, page: null, css: '', styleId });
    return;
  }
  let updateAvailable = false;
  if (ref === 'me') {
    if (routeNamespace) ({ updateAvailable } = await provisionPersonalInstance({ space, prefix: routeNamespace.slice(1), personalBundle, config }));
    else await ensureSelfProvisioned(space, ownerPub);
  }
  const runtime = new AppRuntime(space, { appAdminPub: ownerPub });
  const plan = await runtime.resolveRoute(routeNamespace + userSubPath, timeoutOpt);
  mountEl.quSpace = space;
  renderPage({ mountEl, doc: window.document, templateHtml: plan.templateHtml, page: plan.page, css: plan.css, styleId });
  mountEl.quSelfNodeId = plan.page?.nodeId ?? null; // `self`-node context - see `startApp()`'s own doc comment on this pair.
  mountEl.quSelfKind = plan.page?.kindSchema ?? null;
  // `wireInstalledApps()` goes FIRST, before `wireViews()` - see its own call in `startApp()`'s
  // doc comment on why: `wireViews()` does real async work (resolving + opening each View's live
  // sources) that a form-attaching call must never be stalled behind.
  await wireInstalledApps({ mountEl, doc: window.document, space });
  // Wired regardless of whose `ref` this is, same posture #/<prefix>/cms already has for an
  // ordinary app: write-ACL (self-owned, or an explicit grantContentWriter()) is what actually
  // gates a save, never this UI - a visitor viewing someone ELSE's page sees the same editor,
  // and their save simply fails cleanly (cms-actions.js's own verifyWritesAcked()) unless that
  // owner actually granted them access.
  await wireCms({ mountEl, doc: window.document, space, appAdminPub: ownerPub });
  // `routeNamespace`/`ref` only actually rewrite anything when `routeNamespace` is non-empty (the
  // additive `/u/<ref>/` case) - `view-actions.js`'s own `wireViews()` doc comment on why
  // mode:'multiuser''s own bare-prefix case (`routeNamespace: ''`) deliberately opts out unchanged.
  await wireViews({ mountEl, doc: window.document, space, appAdminPub: ownerPub, routeNamespace, userRef: ref });
  wireRichText({ mountEl, bind: richTextBind });
  // Only ever for `ref === 'me'` (`updateAvailable` stays `false` otherwise) - `installed-apps-
  // actions.js`'s own `wirePersonalUpdateBanner()` doc comment on why an update button on someone
  // ELSE's own personal instance would be actively wrong, not just pointless.
  if (updateAvailable) wirePersonalUpdateBanner({ mountEl, doc: window.document, space, prefix: routeNamespace.slice(1), personalBundle, config });
}

/**
 * Renders a `realm: 'global'` app's own GLOBAL shell content - the exact
 * same `AppRuntime`/`GLOBAL_KINDS`/`globalAppAnchor()`/`wireCms({global:
 * true})` combination `startPlatform()` always used for a global app's bare
 * prefix, factored out here since it now has TWO different call sites: an
 * ordinary `mode: 'global'` app's own bare `#/<prefix>/...` (unchanged),
 * and, for a `mode: 'multiuser'` app - whose bare prefix now resolves to
 * each visitor's OWN space instead (`renderMultiUserRoute()`, right above) -
 * `#/admin/<prefix>/...` instead (`parseAdminSubPath()`, this file's own
 * doc comment on it). Deliberately takes a plain `prefix` string, never a
 * whole `match` object - the admin-delegation call site has no
 * `PlatformRuntime` match for the DELEGATED app, only its prefix from the
 * URL and a lookup in `platform.resolveApps()` confirming it is actually a
 * currently-registered `realm: 'global'` app.
 */
async function renderGlobalShell({ space, mountEl, window, styleId, resolveTimeout, prefix, subPath, richTextBind }) {
  const timeoutOpt = resolveTimeout ? { timeout: resolveTimeout } : undefined;
  const runtime = new AppRuntime(space, { appAdminPub: await globalAppAnchor(prefix), kinds: GLOBAL_KINDS });
  const plan = await runtime.resolveRoute(subPath, timeoutOpt);
  mountEl.quSpace = space;
  renderPage({ mountEl, doc: window.document, templateHtml: plan.templateHtml, page: plan.page, css: plan.css, styleId });
  mountEl.quSelfNodeId = plan.page?.nodeId ?? null; // `self`-node context - see `startApp()`'s own doc comment on this pair.
  mountEl.quSelfKind = plan.page?.kindSchema ?? null;
  await wireInstalledApps({ mountEl, doc: window.document, space });
  await wireCms({ mountEl, doc: window.document, space, appAdminPub: await globalAppAnchor(prefix), global: true, prefix });
  await wireViews({ mountEl, doc: window.document, space, appAdminPub: await globalAppAnchor(prefix), kinds: GLOBAL_KINDS });
  wireRichText({ mountEl, bind: richTextBind });
}

/**
 * Renders a `mode: 'personal'` app's own bare `#/<prefix>/...` - kinds.js's
 * own `platformAppsKind` doc comment on this mode in full: no single
 * relay-admin-authored page here (that's `renderGlobalShell()`'s job, for
 * `'global'` mode), just a READ-ONLY, framework-generated shell around the
 * well-known `<prefix>-aggregate-feed` View - a plain `[data-qu-view]`
 * element `wireViews()` already knows how to wire, exactly the same
 * mechanism (and exactly as reusable/app-agnostic) as any View a CMS page
 * embeds. This function itself never learns what "guestbook" or "blog"
 * even mean - an app opts into `mode: 'personal'` meaning anything at all
 * simply by creating an `adminViewKind` at that name at install time (e.g.
 * `guestbook-bundle.js`'s `installGuestbook()`, sourcing it from the SAME
 * shared list every visitor's own personal instance already writes into -
 * `installed-apps-actions.js`'s own doc comment on `provisionPersonalInstance()`).
 * If no such View exists (an app with no aggregate-feed story at all, or
 * one not yet installed under the new scheme), `wireViews()`'s own
 * `resolveView()` call is a correct no-op - the shell renders with an
 * empty feed, not an error, same "unresolved View, unresolved page" posture
 * every other `[data-qu-view]` already has.
 *
 * Deliberately synthesized markup, not a stored `qu-admin-page` - there is
 * no ROUTE to resolve here (a bare `mode: 'personal'` prefix has no
 * relay-admin-authored page to compete with, and shouldn't grow one just to
 * hold this single `<div>`), the same "framework HTML, never content
 * authored via CMS" posture `renderAdminUnauthorized()` already uses
 * elsewhere in this file. Only the FEED root (`subPath: '/'`) is handled -
 * there is no per-item permalink here yet (an aggregated entry, e.g. a
 * shared-list guestbook signature, has no route of its own to deep-link to
 * in the first place - `view-actions.js`'s own `renderItem()` doc comment
 * on why some items expose raw fields instead of a route); a deeper
 * `subPath` simply renders the same feed, same as any unmatched sub-route
 * elsewhere falling back to its parent.
 */
async function renderAggregateShell({ space, mountEl, window, styleId, prefix, richTextBind }) {
  mountEl.quSpace = space;
  const page = { title: prefix, content: `<div data-qu-view="${prefix}-aggregate-feed"></div>` };
  renderPage({ mountEl, doc: window.document, templateHtml: null, page, css: '', styleId });
  // No real "self" here - `page` above is a synthetic shell object, never a `ContentResolver.resolvePage()`
  // result, so it has no `nodeId`/`kindSchema` to bind to. Cleared explicitly (never left stale from a
  // PREVIOUS route's real page) - see `startApp()`'s own doc comment on this pair.
  mountEl.quSelfNodeId = null;
  mountEl.quSelfKind = null;
  await wireViews({ mountEl, doc: window.document, space, appAdminPub: await globalAppAnchor(prefix), kinds: GLOBAL_KINDS });
  wireRichText({ mountEl, bind: richTextBind });
}

/**
 * @param {{space: import('@qu/space-core').Space, appAdminPub: Uint8Array, mountEl: Element, window: {location: object, document: Document, addEventListener: Function, removeEventListener: Function}, styleId?: string, resolveTimeout?: number, richTextBind?: (textarea: HTMLTextAreaElement) => {refresh: () => void, stop: () => void}}} params
 *   `resolveTimeout` - how long to wait for a route's content to sync before giving up and rendering the "not found" fallback (see @qu/app-core's `ContentResolver`'s own `timeout` param); defaults to that resolver's own default.
 *   `richTextBind` (optional) - `rich-text-actions.js`'s own top doc comment: threaded down to every `wireRichText()` call this function's own render path makes. Omit for the built-in editor (unchanged) - pass e.g. `@qu/space-editor-prosemirror`'s `bindLocalRichText` to opt every `[data-qu-richtext]` textarea on EVERY route this instance renders into the enhanced editor.
 * @returns {{runtime: AppRuntime, router: HashRouter}} - `router.stop()` tears down the hashchange listener; nothing else here needs explicit cleanup.
 */
export function startApp({ space, appAdminPub, mountEl, window, styleId, resolveTimeout, richTextBind }) {
  const runtime = new AppRuntime(space, { appAdminPub });
  // Exposed for @qu/space-components' <qu-view>/<qu-bind>/<qu-list> - see that package's
  // context.js's own doc comment: any Component rendered inside `mountEl` (every page/template's
  // content ends up there via renderPage()'s `mountEl.innerHTML = ...`) resolves its Space by
  // walking up the DOM for the nearest ancestor's `.quSpace` - `mountEl` itself always qualifies.
  mountEl.quSpace = space;
  const router = new HashRouter({
    window,
    onChange: async (route) => {
      const plan = await runtime.resolveRoute(route, resolveTimeout ? { timeout: resolveTimeout } : undefined);
      renderPage({ mountEl, doc: window.document, templateHtml: plan.templateHtml, page: plan.page, css: plan.css, styleId });
      // `self`-node context for @qu/space-components (`resolve.js`'s own doc comment on `self`) -
      // UNLIKE `.quSpace` above (set once, the Space itself never changes mid-session), this is set
      // on EVERY render, because WHICH page is "self" changes on every route. `plan.page` only ever
      // carries `nodeId`/`kindSchema` when it came from `ContentResolver.resolvePage()` (resolver.js's
      // own doc comment on why) - `null` for a "not found" fallback or any OTHER kind of render
      // (an aggregate feed shell, a synthetic page object), a correct "no self to bind to" default a
      // Component's own `self` resolution already treats as "not yet resolvable," same as any other
      // not-yet-available context. Always (re)assigned, never left stale from a PREVIOUS route's page.
      mountEl.quSelfNodeId = plan.page?.nodeId ?? null;
      mountEl.quSelfKind = plan.page?.kindSchema ?? null;
      // `wireInstalledApps()` (guestbook/blog/forum action forms) goes BEFORE `wireViews()`
      // deliberately, here and at every other render call site in this file: `wireViews()` does
      // real async work per `[data-qu-view]` element (`resolveView()` + `openLiveView()`, each a
      // genuine round trip through the relay, `view-actions.js`'s own doc comment) before it
      // resolves - a real, measured gap (tens to ~100ms even in-process, `installed-apps.test.js`'s
      // own regression coverage), not a rounding error. A page's action form is ordinary DOM
      // already sitting in `mountEl` the instant `renderPage()` returns, with ITS OWN listener
      // attached synchronously (no top-level `await` in any of `guestbook-actions.js`/
      // `blog-actions.js`/`forum-actions.js`'s own wiring functions - each one's doc comment
      // explains why) - stalling that attachment behind `wireViews()`'s slower, unrelated work
      // would leave a real window where a fast visitor's `submit` fires on a form with no listener
      // yet, silently lost (confirmed the hard way - `installed-apps.test.js` caught exactly this
      // before this reordering fixed it).
      await wireInstalledApps({ mountEl, doc: window.document, space });
      await wireCms({ mountEl, doc: window.document, space, appAdminPub });
      await wireViews({ mountEl, doc: window.document, space, appAdminPub });
      wireRichText({ mountEl, bind: richTextBind });
    },
  });
  router.start();
  return { runtime, router };
}

/**
 * THE MULTI-APP / PLATFORM VARIANT (architecture.md §7, revised): resolves
 * the CURRENT route against `PlatformRuntime.resolveForPath()` - a
 * registered alias (`qu-platform-apps`), or failing that, the path's first
 * segment tried as a literal owner id - and delegates to an ordinary
 * `AppRuntime`, no differently for a `realm: 'global'` app than for any
 * other app: NEITHER is special-cased on the route STRING (architecture.md
 * §7 - "kein Sonderfall zu normalen Spaces"), only on the resolved match's
 * `realm`, which decides only WHICH Kind SET (`GLOBAL_KINDS` vs. the
 * default) and WHICH owner anchor (`globalAppAnchor(match.prefix)` vs.
 * `match.appAdminPub`) to resolve against - every app lives in the exact
 * SAME `space` (see "One relay Space, not two" in this document's own
 * history / kinds.js's own "GLOBAL APP CONTENT" doc comment: there is no
 * second, separately-membered `Space`/relay-forwarder any more). Only ONE
 * thing here is genuinely framework UI, never Qu content: a route matching
 * NO alias/owner id at all renders `renderLandingPage()` (see this file's
 * own doc comment on it, right above). The built-in admin console
 * (`prefix === 'admin'`) is the ONE `realm: 'global'` app with its own
 * dedicated framework interactivity, `wireAdminConsole()`
 * (`admin-actions.js`) - a bit of interactivity content-declared markup
 * attaches to afterward (its own doc comment explains why that's not a
 * `<script>`-execution loophole), since it is the one app every deployment
 * conventionally has and needs an app-registration form, not a generic
 * page/template/style editor. Every OTHER route (any other app, `realm:
 * 'main'` or `'global'` alike) gets `wireCms()` (`cms-actions.js`) instead,
 * on the exact same "content stays inert markup" terms - a correct no-op
 * unless the resolved page happens to be the built-in CMS editor
 * (`cms-bundle.js`'s `installCms()`).
 *
 * `#/admin` SPECIFICALLY also gets a visibility check
 * (`renderAdminUnauthorized()`, this file's own doc comment on it) BEFORE
 * any of the above - a real, requested UX gap: the console's own content is
 * `'public'`-visibility (readable by anyone - kinds.js's own doc comment),
 * so without this, an ordinary visitor saw the exact same UI a relay-admin
 * does, with every write silently rejected relay-side. Purely cosmetic
 * (`space.isRelayAdmin()`, this Space's own independent view, never the
 * relay's say-so) - the relay's write-ACL was always the only REAL
 * boundary and remains exactly as strict either way. Deliberately scoped
 * to `"admin"` only, not every `realm: 'global'` app - an ordinary global
 * app (a platform-wide chat/calendar/etc., relay-admin-ADMINISTERED but
 * meant for everyone to USE) has no reason to hide itself from non-admins
 * at all, only the admin console's own management UI does.
 *
 * `mode: 'multiuser'` (kinds.js's own doc comment on the three
 * administrable states) FLIPS the default at a bare `#/<prefix>/...`: it
 * now resolves/renders the CURRENTLY signed-in identity's own ordinary,
 * self-owned `'content'`-ACL Kinds (`renderMultiUserRoute()`, `ref: 'me'`)
 * instead of the app's global anchor - the whole point of this mode is
 * that a visitor needs ZERO relay-admin cooperation to get their own
 * space, so making them additionally discover and type `/u/me/` just to
 * reach it would defeat that. `parseMultiUserSubPath()`'s explicit
 * `/u/<ref>/...` form still works, for the one thing a bare prefix can't
 * express: addressing a DIFFERENT identity's space on purpose (`ref` =
 * that identity's own base64url pubkey). The app's own GLOBAL shell
 * content - what a bare prefix used to mean, before this flip - moves to
 * `#/admin/<prefix>/...` instead (`parseAdminSubPath()`/
 * `renderGlobalShell()`, this file's own doc comments on them), reachable
 * only by a relay-admin, same as the admin console's own root - a
 * `multiuser` app's global shell is exactly as relay-admin-administered as
 * a plain `mode: 'global'` app's, it is simply no longer reachable at the
 * BARE prefix once that prefix means "your own space" by default.
 *
 * SELF-PROVISIONING ALSO HAPPENS at the platform's own UNREGISTERED
 * "prefix tried as a literal owner id" fallback (`PlatformRuntime`'s own
 * top doc comment, "TWO KINDS OF MATCH") whenever that pubkey is the
 * CURRENTLY signed-in identity's own - `#/<your-own-base64url-pubkey>/`
 * works as a personal space out of the box, no `mode: 'multiuser'` app
 * involved at all, same self-owned content `#/cms/` (or `#/cms/u/me/`)
 * already provisions - just a shorter, app-independent URL to the exact
 * same place (`deriveOwnerNodeId`/`deriveContentNodeId` derive identically
 * either way). Never triggered for a REGISTERED `realm: 'main'` alias, even
 * one pointing at this same identity - see the inline comment at that
 * check for why.
 * @param {{space: import('@qu/space-core').Space, mountEl: Element, window: object, styleId?: string, resolveTimeout?: number, richTextBind?: (textarea: HTMLTextAreaElement) => {refresh: () => void, stop: () => void}}} params
 *   `richTextBind` - see `startApp()`'s own doc comment on the identical
 *   param. UNLIKE `startApp()`, an explicit value here is only ever a
 *   forced OVERRIDE (tests, or an embedder who wants one editor regardless
 *   of admin config) - when omitted (the real `shell.js` boot path), this
 *   function decides for itself, FRESH on every route render, from the
 *   live `platformAppsKind.platformConfig.richTextEditor` flag
 *   (`kinds.js`'s own doc comment, `dev.js`'s `setPlatformConfig()`,
 *   admin console UI: `admin-console-bundle.js`'s "Editor-Einstellungen"
 *   form) - a relay-admin flipping that switch upgrades every
 *   `[data-qu-richtext]` field platform-wide for every visitor already
 *   connected, no redeploy needed.
 *   `space` - MUST have been constructed with a `relayAdmins` list (see
 *   `Space`'s own constructor doc comment) matching the relay's own
 *   `QU_RELAY_ADMINS` config, or BOTH `qu-platform-apps` AND any global
 *   app's own `qu-admin-*` Kinds fail this Space's own independent ACL
 *   check regardless of what the relay allows (`kinds.js`'s own
 *   `'relay-admins'` doc comments) - `shell.js`'s own boot sequence fetches
 *   that list and passes it through, same as it already does for
 *   `members`. Any visitor's own regular, already-existing identity works
 *   here the moment its pubkey is listed - no separate admin identity to
 *   generate or import.
 * @returns {{platform: PlatformRuntime, router: HashRouter}}
 */
export function startPlatform({ space, mountEl, window, styleId, resolveTimeout, richTextBind }) {
  const platform = new PlatformRuntime(space);
  const timeoutOpt = resolveTimeout ? { timeout: resolveTimeout } : undefined;

  const router = new HashRouter({
    window,
    onChange: async (route) => {
      // Resolved FRESH on every navigation, never cached from `startPlatform()`'s own call time -
      // a relay-admin's `setPlatformConfig({richTextEditor: true})` (the admin console's own
      // "Editor-Einstellungen" form, `admin-actions.js`) then takes effect for every already-
      // connected visitor's very next route render, no reload/redeploy needed - same live-without-
      // reload posture `match.config`/`resolveApps()` already have. An explicit `richTextBind` param
      // (tests, or an embedder who wants to force one editor regardless of the live platform
      // setting) always wins over this - see this function's own doc comment.
      const platformConfig = await platform.resolvePlatformConfig();
      const effectiveRichTextBind = richTextBind ?? (platformConfig.richTextEditor ? bindLocalRichText : undefined);
      const match = await platform.resolveForPath(route, timeoutOpt);
      if (!match) {
        await renderLandingPage({ mountEl, doc: window.document, platform, space });
        return;
      }

      if (match.prefix === 'admin') {
        if (!space.isRelayAdmin()) {
          renderAdminUnauthorized({ mountEl, doc: window.document });
          return;
        }
        // #/admin/<appPrefix>/... - a DIFFERENT registered realm:'global' app's own global shell
        // (parseAdminSubPath()'s own doc comment) - tried BEFORE falling back to the admin console's
        // own UI, so this never shadows the console's own root ("/" never matches it, see that
        // function's own doc comment).
        const delegated = parseAdminSubPath(match.subPath);
        if (delegated) {
          const apps = await platform.resolveApps(timeoutOpt);
          const target = apps.find((a) => a.prefix === delegated.appPrefix && (a.realm ?? 'main') === 'global');
          if (target) {
            await renderGlobalShell({ space, mountEl, window, styleId, resolveTimeout, prefix: target.prefix, subPath: delegated.appSubPath, richTextBind: effectiveRichTextBind });
            return;
          }
        }
        const runtime = new AppRuntime(space, { appAdminPub: await globalAppAnchor('admin'), kinds: GLOBAL_KINDS });
        const plan = await runtime.resolveRoute(match.subPath, timeoutOpt);
        mountEl.quSpace = space;
        renderPage({ mountEl, doc: window.document, templateHtml: plan.templateHtml, page: plan.page, css: plan.css, styleId });
        mountEl.quSelfNodeId = plan.page?.nodeId ?? null; // `self`-node context - see `startApp()`'s own doc comment on this pair.
        mountEl.quSelfKind = plan.page?.kindSchema ?? null;
        await wireInstalledApps({ mountEl, doc: window.document, space });
        wireAdminConsole({ mountEl, doc: window.document, mainSpace: space, platform });
        await wireViews({ mountEl, doc: window.document, space, appAdminPub: await globalAppAnchor('admin'), kinds: GLOBAL_KINDS });
        wireRichText({ mountEl, bind: effectiveRichTextBind });
        return;
      }

      const isGlobal = match.realm === 'global';
      if (isGlobal && match.mode === 'multiuser') {
        // Bare prefix defaults to THIS visitor's own space now - see this function's own doc
        // comment on why the default flipped. `/u/<ref>/...` remains available to address "me"
        // explicitly or another identity's space on purpose.
        const userRoute = parseMultiUserSubPath(match.subPath) ?? { ref: 'me', userSubPath: match.subPath };
        await renderMultiUserRoute({ space, mountEl, window, styleId, resolveTimeout, ...userRoute, richTextBind: effectiveRichTextBind });
        return;
      }

      if (isGlobal) {
        // ADDITIVE personal route, an ordinary mode:'global'/'personal' app's own `/u/<ref>/...` -
        // never replaces what the bare prefix means (that's `mode: 'multiuser'`'s own job, above) -
        // see renderMultiUserRoute()'s own doc comment on `routeNamespace`/`personalBundle` for the
        // full "why prefixed, why this and not multiuser" reasoning. A bare prefix with no `/u/`
        // segment falls through to the global shell (`'global'` mode) or the aggregate feed
        // (`'personal'` mode, right below) exactly as before.
        const userRoute = parseMultiUserSubPath(match.subPath);
        if (userRoute) {
          await renderMultiUserRoute({
            space,
            mountEl,
            window,
            styleId,
            resolveTimeout,
            ...userRoute,
            routeNamespace: `/${match.prefix}`,
            personalBundle: match.personalBundle,
            config: match.config,
            richTextBind: effectiveRichTextBind,
          });
          return;
        }
        if (match.mode === 'personal') {
          await renderAggregateShell({ space, mountEl, window, styleId, prefix: match.prefix, richTextBind: effectiveRichTextBind });
          return;
        }
        await renderGlobalShell({ space, mountEl, window, styleId, resolveTimeout, prefix: match.prefix, subPath: match.subPath, richTextBind: effectiveRichTextBind });
        return;
      }

      // `match.name === null` is `resolveForPath()`'s own signal for the UNREGISTERED "prefix tried
      // as a literal owner id" fallback (this file's own top doc comment on `PlatformRuntime`'s "TWO
      // KINDS OF MATCH") - every identity's own space was ALREADY reachable there with zero
      // relay-admin cooperation, for READING; self-provisioning here too (the SAME
      // `ensureSelfProvisioned()` a `mode: 'multiuser'` app's own `ref: 'me'` already uses) means a
      // brand-new visitor's OWN `#/<their-own-pubkey>/` also becomes a real, working personal space
      // on first visit, not just a dead 404 - one MORE way to reach the exact same self-owned content
      // `#/cms/` (or `#/cms/u/me/`) already provisions, never a second, different space (same
      // `deriveOwnerNodeId(ownerPub, ...)`/`deriveContentNodeId(ownerPub, ...)` derivation regardless
      // of which URL got you there). Scoped to the FALLBACK only, never a REGISTERED `realm: 'main'`
      // alias (even one whose `appAdminPub` happens to be this same identity) - an app-admin who
      // registered their own prefix presumably has (or is about to run) their OWN install script for
      // it, and auto-provisioning a generic "Mein Bereich" starter there instead would be a surprise,
      // not a convenience.
      if (match.name === null && QuCrypto.toBase64(match.appAdminPub) === QuCrypto.toBase64(space.identity.signingPub)) {
        await ensureSelfProvisioned(space, match.appAdminPub);
      }
      const runtime = new AppRuntime(space, { appAdminPub: match.appAdminPub });
      const plan = await runtime.resolveRoute(match.subPath, timeoutOpt);
      mountEl.quSpace = space;
      renderPage({ mountEl, doc: window.document, templateHtml: plan.templateHtml, page: plan.page, css: plan.css, styleId });
      mountEl.quSelfNodeId = plan.page?.nodeId ?? null; // `self`-node context - see `startApp()`'s own doc comment on this pair.
      mountEl.quSelfKind = plan.page?.kindSchema ?? null;
      await wireInstalledApps({ mountEl, doc: window.document, space });
      await wireCms({ mountEl, doc: window.document, space, appAdminPub: match.appAdminPub });
      await wireViews({ mountEl, doc: window.document, space, appAdminPub: match.appAdminPub });
      wireRichText({ mountEl, bind: effectiveRichTextBind });
    },
  });
  router.start();
  return { platform, router };
}
